import React, { useState, useRef, useCallback, useEffect } from 'react';
import QRScanOverlay from './QRScanOverlay';
import { useAuth } from './AuthContext';
import { canSeePurchasingNav, canReviewOffCatalog, canSeeInvoicingNav, canSeeInsights, canSeeWelding, canRecordWeldsHere } from '../lib/permissions';
import { resolveBar, BAR_SLOTS } from '../lib/bottomBar';
import { canReachPage, pageRuleAuth } from '../lib/deepLink';
import WeldingIcon from './WeldingIcon';
// #607 -- the sidebar's collapsed sections, remembered per person.
import { getUiPreference, setUiPreference } from '../lib/userPreferences';
import {
  IconLayoutDashboard,
  IconTools,
  IconPackage,
  IconShieldCheck,
  IconLogout,
  IconHelp,
  IconChevronDown,
  IconChevronRight,
  IconQrcode,
  IconTruck,
  IconAlertTriangle,
  IconTool,
  IconBox,
  IconClipboardList,
  IconReportAnalytics,
  IconAlertCircle,
  IconHome,
  IconShoppingCart,
  IconBuildingStore,
  IconSparkles,
  IconFileInvoice,
  IconClipboardCheck,
  IconArrowsExchange,
  IconExternalLink,
} from '@tabler/icons-react';


// ─── SECTION DEFINITIONS ────────────────────────────────────────────────────

const TOOLS_SECTIONS = [
  { id: 'fleet',         label: 'Fleet',          icon: IconTruck },
  { id: 'tagout',        label: 'Tag-out',         icon: IconAlertTriangle },
  { id: 'servicelog',   label: 'Service Log',     icon: IconTool },
  { id: 'spare',        label: 'Spare Pool',      icon: IconBox },
  { id: 'requests',     label: 'Spare Requests',  icon: IconClipboardList },
  { id: 'inspect',      label: 'Inspections',     icon: IconAlertCircle },
  { id: 'reports',      label: 'Reports',         icon: IconReportAnalytics },
];

const INVENTORY_SECTIONS = [
  { id: 'inv_home',        label: 'Stock Home',  icon: IconHome },
  { id: 'inv_catalog',     label: 'Catalog',     icon: IconPackage },
  { id: 'inv_offcatalog',  label: 'Off-catalog', icon: IconSparkles },
  { id: 'requests_inbox',  label: 'Requests',    icon: IconClipboardList },
  // #391 D9, Session 78n. Reachable by EVERY role from this menu — everyone
  // raises requests — while the bottom-bar slot goes to team_member alone,
  // because only for a field user is it a daily destination. Sits directly
  // below Requests deliberately: they are the two sides of the same object,
  // and a reader who lands on the wrong one should see the right one next to it.
  { id: 'my_requests',     label: 'My requests', icon: IconClipboardCheck },
  // #371 and #425, Session 79.6. Under INVENTORY, not Purchasing, and that is
  // the whole of #425 rather than a filing preference: a transfer has no vendor,
  // no purchase order and no invoice, and Stede spent months describing Houston
  // as a supplier it then had to keep insisting was not one.
  //
  // Sits below My requests for the same reason My requests sits below Requests —
  // "ask another branch" is the neighbour of "ask purchasing", and somebody who
  // lands on the wrong one should see the right one next to it.
  { id: 'transfers',       label: 'Transfers',   icon: IconArrowsExchange },
  // #746, Session 79.22b.6.3 -- between Transfers and the Ledger, as gate 746's
  // F4 places it: welding feeds To make, which lives on Transfers. Shown only
  // where the page rule lets the person in (canReachPage() below): every
  // branch that welds, and admins and executives (746 D5).
  { id: 'welding',         label: 'Welding',     icon: WeldingIcon },
  { id: 'inv_ledger',      label: 'Ledger',      icon: IconReportAnalytics },
];

// #284, Session 74z. Invoicing joins the Purchasing accordion rather than taking
// a rail icon of its own — Colby's explicit constraint at the mockup gate. The
// resulting tension is deliberate and was drawn before it was built: a magenta
// page nested inside an orange section. It holds because the notification domain
// and the navigation location answer different questions.
//
// It carries its own permission gate, unlike the two above, because reconciling
// an invoice is a capability rather than a role — Jennifer and Bridgette are both
// `purchasing` with opposite toggles.
const PURCHASING_SECTIONS = [
  { id: 'purchasing_queue',   label: 'Queue',     icon: IconShoppingCart },
  { id: 'purchasing_vendors', label: 'Vendors',   icon: IconBuildingStore },
  { id: 'purchasing_invoicing', label: 'Invoicing', icon: IconFileInvoice, gate: 'invoicing' },
];

// ─── DESKTOP SIDEBAR ─────────────────────────────────────────────────────────

export default function Sidebar({
  active, setActive, pendingCount, inboxCount = 0, offCatalogCount = 0, invoicingCount = 0,
  isOpen, onClose,
  activeModule, setActiveModule,
}) {
  const auth = useAuth();
  const { profile, signOut, isAdmin, isManager, isExecutive, isTeamMember, isPurchasing } = auth;
  // #746 -- the page rule's input from its one helper (deepLink.js).
  const ruleAuth = pageRuleAuth(auth);
  const [hoveredModule, setHoveredModule] = useState(null);
  // #607, Session 79.21c, on Colby's ruling. All three opened EVERY time,
  // which is what made the phone drawer twenty rows long. They now remember,
  // per person, so somebody who lives in Inventory collapses Tools once and
  // never sees it again — Slack's answer to the same problem, and the one the
  // research turned up.
  //
  // OPEN IS STILL THE DEFAULT for anybody who has never touched them, so
  // nothing changes for a new hire and nothing changes on a database that has
  // not had the column yet.
  const [toolsExpanded, setToolsExpanded] = useState(
    () => getUiPreference(profile, 'sidebar_tools_open', true));
  const [inventoryExpanded, setInventoryExpanded] = useState(
    () => getUiPreference(profile, 'sidebar_inventory_open', true));
  const [purchasingExpanded, setPurchasingExpanded] = useState(
    () => getUiPreference(profile, 'sidebar_purchasing_open', true));

  // The profile arrives after the first render on a cold load, so the initial
  // state above is read before there is anything to read. This syncs once it
  // lands — and ONLY from a profile that actually carries the key, so it can
  // never slam a section shut that the reader just opened.
  useEffect(() => {
    if (!profile?.ui_preferences) return;
    const p = profile.ui_preferences;
    if (p.sidebar_tools_open !== undefined) setToolsExpanded(p.sidebar_tools_open);
    if (p.sidebar_inventory_open !== undefined) setInventoryExpanded(p.sidebar_inventory_open);
    if (p.sidebar_purchasing_open !== undefined) setPurchasingExpanded(p.sidebar_purchasing_open);
  }, [profile?.ui_preferences]);

  // One toggle for all three: flip the state now so the section responds to the
  // tap immediately, and save in the background. The save is best-effort and
  // never awaited — see setUiPreference.
  const toggleSection = (setter, key) => setter(v => {
    const next = !v;
    setUiPreference(profile, key, next);
    return next;
  });
  const flyoutTimeoutRef = useRef(null);
  // #606 -- where the hovered icon sits inside the rail.
  const [flyoutTop, setFlyoutTop] = useState(0);
  const flyoutRef = useRef(null);

  // Filter Tools sections by role — preserve existing role restrictions
  const canSeePurchasing = canSeePurchasingNav({ isAdmin, isManager, isExecutive, isPurchasing });
  // Issue #243. Same role set as purchasing nav today, but kept on its own
  // predicate so changing one does not silently move the other.
  const canSeeOffCatalog = canReviewOffCatalog({ isAdmin, isManager, isExecutive, isPurchasing });
  const canSeeInvoicing = canSeeInvoicingNav({ isAdmin, isManager, isExecutive, isPurchasing, profile });
  // #308, Session 74z6. Insights sits ABOVE the module rail rather than in it.
  // The rail answers "which module am I in"; this surface is in none of them —
  // it reports across purchasing, inventory and later tools. See gate decision D2.
  const canSeeInsightsNav = canSeeInsights({ isAdmin, isManager, isExecutive, isPurchasing });
  const visiblePurchasingSections = PURCHASING_SECTIONS.filter(
    s => s.gate !== 'invoicing' || canSeeInvoicing
  );

  // #391 D10, Session 78n. The inventory rail now also passes through
  // canReachPage(), which is what removes `requests_inbox` from a team member's
  // menu: D10 takes the approval queue out of their reach ENTIRELY — sidebar
  // entry, landing-page choice and route fallback — because leaving it
  // reachable would leave the defect sitting next to its own fix.
  //
  // The off-catalog test is kept as its own term rather than folded in, even
  // though canReviewOffCatalog and the route guard's PURCHASING_STAFF_ONLY
  // resolve to the same predicate today. #243 separated them on purpose so that
  // changing one does not silently move the other, and collapsing them here
  // would undo that decision as a side effect of an unrelated change.
  const visibleInventorySections = INVENTORY_SECTIONS.filter(s =>
    !(s.id === 'inv_offcatalog' && !canSeeOffCatalog)
    && canReachPage(ruleAuth, s.id)
  );

  const visibleToolsSections = TOOLS_SECTIONS.filter(s => {
    if (s.id === 'spare' && isTeamMember) return false;
    if (s.id === 'requests' && isTeamMember) return false;
    if (s.id === 'servicelog' && isTeamMember) return false;
    // #76, Session 78l -- a `reports && isExecutive` branch used to sit here,
    // immediately after the line above. It could never change the outcome for
    // any role: a team_member has already returned false, and every other role
    // reaches the same `return true` below. Removed rather than left as an
    // unreachable branch that reads like a rule.
    if (s.id === 'reports' && isTeamMember) return false;
    return true;
  });

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const handleSectionClick = (id) => {
    setActive(id);
    setActiveModule('tools');
    setHoveredModule(null);
    onClose?.();
  };

  const handleInventoryClick = (id) => {
    setActive(id);
    setActiveModule('inventory');
    setHoveredModule(null);
    onClose?.();
  };

  // #606, Session 79.21c, on Colby's report with a screenshot. The flyout was
  // pinned to `top: 0`, so hovering Purchasing -- the third icon down -- put its
  // menu up beside Insights, floating away from the icon that summoned it.
  //
  // `offsetTop` is measured against `.sidebar-rail`, which is positioned
  // (`position: sticky` since #768, pinned below the top bar; `relative`
  // before it) and is what the flyout is absolutely positioned against, so the
  // two agree without any page-coordinate arithmetic.
  const handleMouseEnterModule = (mod, e) => {
    clearTimeout(flyoutTimeoutRef.current);
    if (e?.currentTarget) setFlyoutTop(e.currentTarget.offsetTop);
    setHoveredModule(mod);
  };

  const handleMouseLeaveArea = () => {
    flyoutTimeoutRef.current = setTimeout(() => {
      setHoveredModule(null);
    }, 120);
  };

  const handleMouseEnterFlyout = () => {
    clearTimeout(flyoutTimeoutRef.current);
  };

  // ── COLLAPSED RAIL ───────────────────────────────────────────────────────

  if (!isOpen) {
    return (
      <div className="sidebar-rail" onMouseLeave={handleMouseLeaveArea}>

        {/* Module icons */}
        <div className="rail-modules">

          {/* Insights — shell level, deliberately ABOVE the modules and divided
              from them, but INSIDE this container rather than a sibling of it.
              `.sidebar-rail` is `justify-content: space-between`, so every extra
              direct child gets spread down the full height of the rail — adding
              this icon and its divider as siblings orphaned it against the
              topbar with the modules stranded mid-rail. Caught on the render.

              Setting activeModule to 'insights' is what stops a module icon
              staying lit underneath: every `activeModule === '<module>'`
              comparison goes false, so exactly one icon reads as active. */}
          {canSeeInsightsNav && (
            <>
              <div
                className={`rail-icon rail-icon--insights${activeModule === 'insights' ? ' rail-icon--active' : ''}`}
                onClick={() => { setActive('insights'); setActiveModule('insights'); setHoveredModule(null); onClose?.(); }}
                onMouseEnter={() => setHoveredModule(null)}
                title="Insights"
              >
                <IconLayoutDashboard size={20} />
              </div>
              <div style={{ width: 22, height: 1, background: 'rgba(255,255,255,0.18)', margin: '2px auto' }} />
            </>
          )}

          {/* Tools */}
          <div
            className={`rail-icon${activeModule === 'tools' ? ' rail-icon--active' : ''}`}
            onMouseEnter={e => handleMouseEnterModule('tools', e)}
          >
            <IconTools size={20} />
          </div>

          {/* Inventory */}
          <div
            className={`rail-icon${activeModule === 'inventory' ? ' rail-icon--active' : ''}`}
            onMouseEnter={e => handleMouseEnterModule('inventory', e)}
          >
            <IconPackage size={20} />
          </div>

          {/* Purchasing */}
          {canSeePurchasing && (
            <div
              className={`rail-icon${activeModule === 'purchasing' ? ' rail-icon--active' : ''}`}
              onMouseEnter={e => handleMouseEnterModule('purchasing', e)}
            >
              <IconShoppingCart size={20} />
            </div>
          )}

          {/* Safety */}
          <div
            className="rail-icon rail-icon--disabled"
            onMouseEnter={e => handleMouseEnterModule('safety', e)}
          >
            <IconShieldCheck size={20} />
          </div>
        </div>

        {/* Sign out */}
        <div className="rail-signout" onClick={signOut}>
          <IconLogout size={18} />
        </div>

        {/* Flyout panel */}
        {hoveredModule && (
          <div
            ref={flyoutRef}
            className="sidebar-flyout"
            // #606 -- beside the icon that opened it. The panel is clamped so a
            // low icon's menu cannot run off the bottom of the window: Safety is
            // the last icon on the rail and its list is the shortest, but a
            // module gaining screens would otherwise push its own menu off.
            style={{ top: flyoutTop }}
            onMouseEnter={handleMouseEnterFlyout}
            onMouseLeave={handleMouseLeaveArea}
          >
            {hoveredModule === 'tools' && (
              <>
                <div className="flyout-header">Tools</div>
                <div className="flyout-body">
                  {visibleToolsSections.map(s => {
                    const Icon = s.icon;
                    return (
                      <button
                        key={s.id}
                        className={`flyout-item${active === s.id ? ' flyout-item--active' : ''}`}
                        onClick={() => handleSectionClick(s.id)}
                      >
                        <Icon size={15} />
                        {s.label}
                        {s.id === 'requests' && pendingCount > 0 && (
                          <span className="flyout-badge">{pendingCount}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            {hoveredModule === 'inventory' && (
              <>
                <div className="flyout-header">Inventory</div>
                <div className="flyout-body">
                  {/* #77 + Colby's ruling at Session 78l -- the team_member
                      exclusion on inv_catalog is GONE, and it is a grant rather
                      than a tidy-up.

                      The two sidebar surfaces disagreed: this collapsed-rail
                      flyout filtered Catalog out for a team_member while the
                      expanded accordion below rendered it, so the same person
                      saw different navigation depending on sidebar state. #77
                      logged that as an inconsistency without saying which side
                      was right. It is this one.

                      Nothing was ever protected by hiding it. Neither
                      InventoryCatalogPage nor InventoryItemDetail renders a
                      cost figure -- Item Detail fetches avg_unit_cost only to
                      stamp it onto a transaction -- and canAddInventoryItem /
                      canEditInventoryItem are both admin-or-manager, so the
                      page is read-only for a team_member by construction.
                      canReachPage already allowed it: inv_catalog is not in
                      TEAM_MEMBER_BLOCKED, so it was offered in their
                      landing-page picker while being hidden from their rail.

                      The load-bearing reason is #369. Item Detail's mobile
                      action rail was built for team members and canAdjustInventory
                      admits them since 78g, so that rail is how a field user
                      adjusts stock on a phone -- and Catalog is the route to it.
                      Hiding Catalog orphaned the go-live fix. */}
                  {visibleInventorySections.map(s => {
                    const Icon = s.icon;
                    return (
                      <button
                        key={s.id}
                        className={`flyout-item${active === s.id ? ' flyout-item--active' : ''}`}
                        onClick={() => handleInventoryClick(s.id)}
                      >
                        <Icon size={15} />
                        {s.label}
                        {s.id === 'requests_inbox' && inboxCount > 0 && (
                          <span className="flyout-badge">{inboxCount}</span>
                        )}
                        {s.id === 'inv_offcatalog' && offCatalogCount > 0 && (
                          <span className="flyout-badge">{offCatalogCount}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            {hoveredModule === 'purchasing' && (
              <>
                <div className="flyout-header">Purchasing</div>
                <div className="flyout-body">
                  {visiblePurchasingSections.map(s => {
                    const Icon = s.icon;
                    const isInvoicing = s.id === 'purchasing_invoicing';
                    return (
                      <button
                        key={s.id}
                        className={`flyout-item${isInvoicing ? ' flyout-item--invoicing' : ''}${active === s.id ? ' flyout-item--active' : ''}`}
                        onClick={() => { setActive(s.id); setActiveModule('purchasing'); setHoveredModule(null); onClose?.(); }}
                      >
                        <Icon size={15} />
                        {s.label}
                        {isInvoicing && invoicingCount > 0 && (
                          <span className="flyout-badge flyout-badge--invoicing">{invoicingCount}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            {hoveredModule === 'safety' && (
              <>
                <div className="flyout-header">Safety</div>
                <div className="flyout-body flyout-body--soon">
                  <span>Coming soon</span>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    );
  }

  // ── EXPANDED ACCORDION ────────────────────────────────────────────────────

  return (
    <>
      <div className="sidebar-expanded">
        <div className="sidebar-expanded-scroll">

          {/* Insights — a destination, not an accordion. It has no sub-sections
              and it belongs to no module, so it renders as a single row above
              the three module groups rather than as a fourth group. #308, D2. */}
          {canSeeInsightsNav && (
            <div className="accordion-group">
              <button
                className={`accordion-header${active === 'insights' ? ' accordion-header--active' : ''}`}
                onClick={() => { setActive('insights'); setActiveModule('insights'); onClose?.(); }}
              >
                <span className="accordion-header-left">
                  <span className="nav-mark"><IconLayoutDashboard size={15} /></span>
                  <span>Insights</span>
                </span>
              </button>
            </div>
          )}

          {/* Tools accordion */}
          <div className="accordion-group">
            <button
              className="accordion-header"
              onClick={() => toggleSection(setToolsExpanded, 'sidebar_tools_open')}
            >
              <span className="accordion-header-left">
                <IconTools size={17} />
                <span>Tools</span>
              </span>
              {toolsExpanded
                ? <IconChevronDown size={15} />
                : <IconChevronRight size={15} />
              }
            </button>
            {toolsExpanded && (
              <div className="accordion-body">
                {visibleToolsSections.map(s => {
                  const Icon = s.icon;
                  return (
                    <button
                      key={s.id}
                      className={`accordion-item${active === s.id ? ' accordion-item--active' : ''}`}
                      onClick={() => handleSectionClick(s.id)}
                    >
                      <Icon size={14} />
                      {s.label}
                      {s.id === 'requests' && pendingCount > 0 && (
                        <span className="nav-badge">{pendingCount}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Inventory accordion */}
          <div className="accordion-group">
            <button
              className="accordion-header"
              onClick={() => toggleSection(setInventoryExpanded, 'sidebar_inventory_open')}
            >
              <span className="accordion-header-left">
                <IconPackage size={17} />
                <span>Inventory</span>
              </span>
              {inventoryExpanded
                ? <IconChevronDown size={15} />
                : <IconChevronRight size={15} />
              }
            </button>
            {inventoryExpanded && (
              <div className="accordion-body">
                {visibleInventorySections.map(s => {
                  const Icon = s.icon;
                  return (
                    <button
                      key={s.id}
                      className={`accordion-item${active === s.id ? ' accordion-item--active' : ''}`}
                      onClick={() => handleInventoryClick(s.id)}
                    >
                      <Icon size={14} />
                      {s.label}
                      {s.id === 'requests_inbox' && inboxCount > 0 && (
                        <span className="nav-badge">{inboxCount}</span>
                      )}
                      {s.id === 'inv_offcatalog' && offCatalogCount > 0 && (
                        <span className="nav-badge">{offCatalogCount}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Purchasing accordion */}
          {canSeePurchasing && (
            <div className="accordion-group">
              <button
                className="accordion-header"
                onClick={() => toggleSection(setPurchasingExpanded, 'sidebar_purchasing_open')}
              >
                <span className="accordion-header-left">
                  <IconShoppingCart size={17} />
                  <span>Purchasing</span>
                </span>
                {purchasingExpanded
                  ? <IconChevronDown size={15} />
                  : <IconChevronRight size={15} />
                }
              </button>
              {purchasingExpanded && (
                <div className="accordion-body">
                  {visiblePurchasingSections.map(s => {
                    const Icon = s.icon;
                    const isInvoicing = s.id === 'purchasing_invoicing';
                    return (
                      <button
                        key={s.id}
                        className={`accordion-item${isInvoicing ? ' accordion-item--invoicing' : ''}${active === s.id ? ' accordion-item--active' : ''}`}
                        onClick={() => { setActive(s.id); setActiveModule('purchasing'); onClose?.(); }}
                      >
                        <Icon size={14} />
                        {s.label}
                        {isInvoicing && invoicingCount > 0 && (
                          <span className="flyout-badge flyout-badge--invoicing">{invoicingCount}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Safety accordion — locked */}
          <div className="accordion-group">
            <div className="accordion-header accordion-header--disabled">
              <span className="accordion-header-left">
                <IconShieldCheck size={17} />
                <span>Safety</span>
              </span>
              <span className="soon-badge">Soon</span>
            </div>
          </div>

        </div>

        {/* HELP CENTER — gate D17's second placement, restyled at Colby's
            instruction after he saw the first attempt: *"this link in the
            sidebar is ugly, can you not make it render as something that
            matches the styling of the app?"* He was right. It shipped as a
            bare <a class="sidebar-nav-item"> — a class this file does not use
            anywhere else and index.css does not define — so it fell back to the
            browser default and rendered as blue underlined text in a sidebar
            that contains no other link.

            It now renders as .accordion-group / .accordion-header, the same
            box as TOOLS, INVENTORY and SAFETY above it. That is not only
            cosmetic: it says what the thing IS. A module row is a destination
            you go to on purpose, which is exactly what a help centre is.

            THREE DECISIONS WORTH KEEPING, all Colby's call at 78o:

            1. It goes to the guides INDEX, always — never a deep link. The
               sidebar's job is the browse-and-print front door: thirteen guides,
               each named with what it covers and who it is for. The
               screen-specific deep link is the ACCOUNT MENU's job (Topbar.js).
               Two entry points, two different questions — "what do I print for
               the new hire" and "I am stuck on this screen right now" — and
               collapsing them into one loses whichever you drop.

            2. NO COUNT BADGE. A number beside a nav row means "things needing
               your attention" everywhere else in this sidebar — the off-catalog
               review count sits three rows up. Thirteen guides is not a queue,
               and borrowing the queue's visual language to say "we have some
               documents" would train people to ignore the badge that matters.

            3. Named HELP CENTER rather than USER GUIDES, because it survives
               later additions — FAQs, a what's-new page, video — without the
               label becoming a lie.

            Pinned below the scroll area rather than inside it, so it cannot
            scroll out of reach on a phone, and deliberately NOT gated on a
            role: the guides describe what each role can do, so the person who
            needs them most is the one with the fewest permissions. */}
        <div className="accordion-group sidebar-help-group">
          <a
            className="accordion-header"
            href="/guides/index.html"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => onClose?.()}
          >
            <span className="accordion-header-left">
              <IconHelp size={17} />
              <span>Help center</span>
            </span>
            {/* Says the row opens a new tab. The other rows in this column use
                this slot for a chevron or a SOON badge, so leaving it empty
                would read as an unfinished row rather than a plain one. */}
            <IconExternalLink size={14} style={{ opacity: 0.4 }} />
          </a>
        </div>

        {/* Footer */}
        <div className="sidebar-footer">
          <div className="sidebar-footer-avatar">
            {getInitials(profile?.full_name)}
          </div>
          <div className="sidebar-footer-info">
            <div className="sidebar-footer-name">{profile?.full_name}</div>
            <div className="sidebar-footer-role">{profile?.role?.replace('_', ' ')}</div>
            <div className="sidebar-footer-branch">{profile?.branch_name}</div>
          </div>
          <button className="sidebar-footer-signout" onClick={signOut} title="Sign out">
            <IconLogout size={16} />
          </button>
        </div>
      </div>

      {/* Overlay to close */}
      <div className="sidebar-overlay open" onClick={onClose} />
    </>
  );
}

// ─── BOTTOM NAV (mobile) ─────────────────────────────────────────────────────

export function BottomNav({ active, setActive, activeModule, setActiveModule, pendingCount, inboxCount = 0 }) {
  const auth = useAuth();
  const { profile, signOut, isAdmin, isManager, isExecutive, isPurchasing } = auth;

  // The Coming Soon toast that used to live here went with the Safety slot at
  // #167's D12. It had exactly one caller -- Safety's bottom-bar button -- so
  // once that slot stopped existing the toast could never fire again. Removed
  // rather than left as an unreachable branch.

  const [scanOpen, setScanOpen] = useState(false);

  const handleScan = () => {
    setScanOpen(true);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const canSeePurchasing = canSeePurchasingNav({ isAdmin, isManager, isExecutive, isPurchasing });
  const canSeeInsightsNav = canSeeInsights({ isAdmin, isManager, isExecutive, isPurchasing });
  const canSeeInvoicing = canSeeInvoicingNav({ isAdmin, isManager, isExecutive, isPurchasing, profile });

  // #167 D4/D5/D11/D12 -- the bar's shape moved to src/lib/bottomBar.js at
  // Session 78l, because Company settings -> Navigation now shows an
  // administrator the same table and the two must not derive it separately.
  // Everything that was written here about WHY each role gets what it gets
  // moved with it; this file keeps only what rendering needs, which is icons.
  const ICONS = {
    stock: IconPackage,
    requests: IconClipboardList,
    my_requests: IconClipboardCheck,
    buying: IconShoppingCart,
    insights: IconReportAnalytics,
    invoicing: IconFileInvoice,
    tools: IconTools,
    // #746, Session 79.22b.6.3 -- Grok's frame-and-weld icon, as drawn on the bar.
    welding: WeldingIcon,
  };
  // resolveBar rather than barForRole: the person's own arrangement wins over
  // their role's, which is #167 D1's second layer and the same precedence
  // landingPageFor() applies to the front door.
  const { left, right } = resolveBar(profile, {
    canSeePurchasing,
    canSeeInsights: canSeeInsightsNav,
    canSeeInvoicing,
    // #746 -- the slot leads somewhere only where the page rule lets the person
    // in. Takes the whole auth: canSeeWelding() reads the branch (746 D5).
    canSeeWelding: canSeeWelding(auth),
    // ... and placed after Stock by default for whoever records welds at a
    // branch that welds (746 D24).
    canRecordWeldsHere: canRecordWeldsHere(auth),
  });

  // #372 — Scan holds the centre whatever each side ends up holding.
  //
  // .bottom-nav-item is flex: 1, so the bar divides itself by however many
  // children it has and Scan lands centred only when the two sides are equal.
  // Padding to the longer side is the general form of the fix that shipped
  // earlier this session for the missing-Purchasing case, and it now has to be
  // general, because D11 gives different roles different numbers of slots.
  //
  // Reserving the cell rather than re-centring the bar is still the point:
  // every icon sits at the same x for every role, not only Scan. D7 makes Scan
  // immovable precisely because it is the muscle-memory anchor, and an anchor
  // that moves with the viewer's permissions is not one.
  const perSide = Math.max(left.length, right.length);
  const pad = list => [...list, ...Array(perSide - list.length).fill(null)];

  const renderSlot = (key, i, side) => {
    if (key === null) return <span key={`${side}-pad-${i}`} className="bottom-nav-item" aria-hidden="true" />;
    const slot = BAR_SLOTS[key];
    const Icon = ICONS[key];
    const isActive = key === 'tools'
      ? activeModule === 'tools'
      : key === 'stock'
        // #746: not on Welding either -- Welding has its own slot, and two lit
        // slots would say the person is in two places.
        ? activeModule === 'inventory' && active !== 'requests_inbox' && active !== 'welding'
        : active === slot.page;
    return (
      <button
        key={key}
        className={`bottom-nav-item${isActive ? ' active' : ''}`}
        onClick={() => { setActiveModule(slot.module); setActive(slot.page); }}
        aria-label={slot.label}
      >
        <Icon size={22} />
        {slot.badge && inboxCount > 0 && (
          <span className="nav-badge bottom-nav-badge">{inboxCount}</span>
        )}
        {isActive && <span className="bottom-nav-dot" />}
      </button>
    );
  };

  return (
    <>
      <nav className="bottom-nav" role="navigation" aria-label="Main navigation">
        <div className="bottom-nav-inner">

          {pad(left).map((key, i) => renderSlot(key, i, 'left'))}

          {/* Scan - raised centre, fixed, and not configurable. D7: it is the
              only slot that is an action rather than a destination, it is the
              one-tap entry to the QR path #320 exists to protect, and it is the
              anchor the two-taps-under-three-seconds standard is measured from. */}
          <button
            className="bottom-nav-scan"
            onClick={handleScan}
            aria-label="Scan QR code"
          >
            <IconQrcode size={24} color="#0F0F0F" />
          </button>

          {pad(right).map((key, i) => renderSlot(key, i, 'right'))}

        </div>
      </nav>

      {scanOpen && (
        <QRScanOverlay onClose={() => setScanOpen(false)} />
      )}

    </>
  );
}